import _ from 'lodash';
import template from './AggregatedEventDetailsModal.html';

/* @ngInject */
export default function GraphPanelService(
  $q, $http, $log, CalculateValuesService, appConfig, $uibModal, GraphRequestQueue
) {
  const service = this;

  let graphModel = {};
  const week = 7;
  const day = 24;
  const hour = 60;
  const sec = 1000;
  const dayMs = day * hour * hour * sec;

  service.getEventsForPeriodForEquipment = getEventsForPeriodForEquipment;
  service.getModel = getModel;
  service.updateModelFromEvent = updateModelFromEvent;

  // Whether a value is a real, plottable number — unwraps live objects ({ value }) and downsampled
  // customBars triples ([min, avg, max]).
  function valueIsFinite(value) {
    if (_.isArray(value)) {
      return _.some(value, valueIsFinite);
    }
    if (_.isObject(value)) {
      // .value may be a scalar or a [min, avg, max] triple (customBars).
      return valueIsFinite(value.value);
    }
    // Booleans are plottable — createGraphDataObj graphs them as Number(val) and the y-axis formatter
    // renders them back as true/false — so a boolean-only panel (padlocks, digital inputs) has data.
    return _.isFinite(value) || _.isBoolean(value);
  }

  // An event has plottable data if any of its readings is a finite number. Distinguishes a panel that
  // is reporting-but-all-null (dead sensor) from one with real data — the former should show a clean
  // empty state, not a degenerate empty chart.
  function eventHasData(event) {
    return Boolean(event && event.data && _.some(_.values(event.data), valueIsFinite));
  }
  service.deleteModel = deleteModel;
  service.openAggregatedEventDetailsModal = openAggregatedEventDetailsModal;

  // options.startDateOverride (ms) requests an arbitrary sub-range (e.g. on zoom) so the server returns
  // that range at a finer resolution; when omitted the panel's configured window is used.
  // options.canceler is a promise — resolving it aborts the in-flight XHR, so a newer request for the
  // same panel+equipment can supersede this one.
  function getEventsForPeriodForEquipment(equipment, panel, endDate = Date.now(), options = {}) {
    const { startDateOverride, canceler } = options;
    if (!graphModel[panel.id]) {
      graphModel[panel.id] = {};
    }

    if (!graphModel[panel.id][equipment.id]) {
      graphModel[panel.id][equipment.id] = {
        equipment,
        latestReadings: {},
        events: []
      };
    }

    // if number of days is specified, use that number :: default to 7 days
    const days = parseInt(_.get(panel, ['config', equipment.id, 'daysOfDataToPull'], week), 10);
    const then = _.isFinite(startDateOverride) ? startDateOverride : endDate - (days * dayMs);
    // maxQueryDateRange is the server's span guard; size it to the actual requested span.
    const spanDays = Math.max(1, Math.ceil((endDate - then) / dayMs));
    const route = equipment.equipmentType === 'totalFlowMeters' ? 'totalFlowDataInPeriod' : 'dataInPeriodForGraphing';
    const { listenForEvent } = panel.config[equipment.id] || {};
    const url = `${appConfig.apiBaseUrl}/event/${route}/${equipment.id}` +
      `?maxQueryDateRange=${spanDays}&startDate=${then}&endDate=${endDate}` +
      `${listenForEvent ? `&eventName=${listenForEvent}` : ''}`;
    const httpConfig = { withCredentials: true };
    if (canceler) {
      httpConfig.timeout = canceler; // resolving this promise aborts the XHR
    }
    // key coalesces same panel+equipment requests still waiting in the shared queue.
    const requestKey = `${panel.id}:${equipment.id}`;
    return GraphRequestQueue.enqueue(() => $http.get(url, httpConfig), { key: requestKey })
      .then(({ data }) => {
        if (data.events) {
          // call service to calculate extra data points
          if (data.equipment.equipmentType === 'tanks') {
            data.events = CalculateValuesService.calculateBarrels(data.events);
          } else if (data.equipment.equipmentType === 'cameras') {
            data.events = CalculateValuesService.convertDecibels(data.events);
          }

          graphModel[panel.id][equipment.id].events = data.events;
          // Server signals when the payload is bucketed to [min, avg, max] (Dygraph customBars).
          graphModel[panel.id][equipment.id].downsampled = Boolean(data.downsampled);
          graphModel[panel.id][equipment.id].bucketMs = data.bucketMs;
          // Plottable-data flag drives chart-vs-clean-empty-state; computed once here, not per digest.
          graphModel[panel.id][equipment.id].hasData = _.some(data.events, eventHasData);
          for (let i = 0, len = data.events.length; i < len; i++) {
            updateLatestReadings(panel, equipment.id, data.events[i]);
          }
        }
        return equipment.id;
      })
      .catch((error) => {
        // Superseded (queue-coalesced) or cancelled (XHR aborted) requests are expected — stay quiet.
        const cancelled = Boolean(error && (error.superseded || error.xhrStatus === 'abort'));
        if (!cancelled) {
          $log.error('Error: ', error);
        }
        return $q.reject(cancelled ? error : error && error.data);
      });
  }

  function getModel(panelId) {
    if (!graphModel[panelId]) {
      graphModel[panelId] = {};
    }
    return graphModel[panelId];
  }

  function updateModelFromEvent(panel, eventObj) {
    if (!graphModel[panel.id]) {
      graphModel[panel.id] = {};
    }

    // create calculated value
    if (eventObj.equipment.equipmentType === 'tanks') {
      eventObj = CalculateValuesService.calculateBarrels([eventObj])[0];
    } else if (eventObj.equipment.equipmentType === 'cameras') {
      eventObj = CalculateValuesService.convertDecibels([eventObj])[0];
    }


    if (!graphModel[panel.id][eventObj.equipment.id]) {
      graphModel[panel.id][eventObj.equipment.id] = {
        equipment: eventObj.equipment,
        latestReadings: {}
      };
    }

    if (!graphModel[panel.id][eventObj.equipment.id].events) {
      graphModel[panel.id][eventObj.equipment.id].events = [];
    }

    // Match the customBars format of whatever is currently loaded: wrap the live value as [min,avg,max]
    // when the panel is configured for downsampling OR the loaded data was downsampled by the server —
    // otherwise a live point appended to a customBars graph would be a bare scalar and render wrong.
    const configuredDownsampling = _.get(panel, ['config', eventObj.equipment.id, 'performDataDownsampling'], false);
    const loadedDownsampled = _.get(graphModel, [panel.id, eventObj.equipment.id, 'downsampled'], false);
    if (configuredDownsampling || loadedDownsampled) {
      _.forOwn(eventObj.data, (dataPoint) => {
        if (_.isFinite(dataPoint.value)) {
          dataPoint.value = [dataPoint.value, dataPoint.value, dataPoint.value];
        }
      });
    }

    const index = _.sortedLastIndexBy(graphModel[panel.id][eventObj.equipment.id].events, eventObj, 'eventDate');
    graphModel[panel.id][eventObj.equipment.id].events.splice(index, 0, eventObj);
    // A live reading can flip an all-null panel into having data (e.g. a sensor recovering).
    if (eventHasData(eventObj)) {
      graphModel[panel.id][eventObj.equipment.id].hasData = true;
    }
    updateLatestReadings(panel, eventObj.equipment.id, eventObj);
  }

  function updateLatestReadings(panel, equipmentId, { data: eventData }) {
    let keys;
    if (panel && panel.id && equipmentId && eventData) {
      const { includeInGraph = [], excludeFromGraph = [] } = _.get(panel, ['config', equipmentId], {});

      // includeInGraph overrides excludeFromGraph
      keys = includeInGraph.length ?
        includeInGraph :
        excludeFromGraph.length ?
          _.difference(_.keys(eventData), excludeFromGraph) :
          _.keys(eventData);

      _.forEach(_.sortBy(keys), (key) => {
        if (!_.isNil(_.get(eventData, [key, 'value']))) {
          graphModel[panel.id][equipmentId].latestReadings[key] = eventData[key].value;
        } else if (!_.isNil(eventData[key]) && !_.isPlainObject(eventData[key])) {
          const raw = eventData[key];
          // Downsampled series arrive as [min, avg, max]; surface the avg as the latest reading.
          // ACCEPTED TRADE-OFF (do not "fix" without a UX decision): on a downsampled window this is
          // the newest *bucket's* average, not the newest sample, and the bucket widens with
          // daysOfDataToPull. The panel exists to show the trend, the value is never wrong for the
          // window it covers, and the alternatives (an extra request just for the latest point, or a
          // per-panel label) cost more than this secondary readout is worth.
          graphModel[panel.id][equipmentId].latestReadings[key] = _.isArray(raw) ? raw[1] : raw;
        }
      });
    }
  }

  /**
   * Open a modal containing the aggregated event details
   *
   * @param  {object} aggregatedPoint Dygraph UI values for the data point in the graph and page
   * @param  {Array<object>} allAggregatedEvents AggregatedEvents for the dataPoint series
   * @returns {object} the result of the call to $modal.open
   */
  function openAggregatedEventDetailsModal(aggregatedPoint, allAggregatedEvents) {
    return $uibModal.open({
      template,
      controller: 'AggregatedEventDetailsModalController',
      controllerAs: 'AggregatedEventDetailsModalCtrl',
      size: 'sm',
      resolve: {
        allAggregatedEvents: () => allAggregatedEvents,
        aggregatedEventDygraph: () => aggregatedPoint
      }
    });
  }

  // Resets the model for ALL panels, not just the caller's — this is deliberate and safe, despite how
  // it reads. Each graphPanel component holds a reference to the inner graphModel[panelId] object, so
  // reassigning the outer variable leaves live panels working off their retained slice; a panel that is
  // later re-created simply refetches. Verified, pre-existing, and intentionally not narrowed — scoping
  // it per panel would be a behavioral change with no current benefit.
  function deleteModel() {
    graphModel = {};
  }
}
